import React, { useMemo } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { colors } from './Theme/WebTheme';
import { FONT_FAMILY } from '../common/sharedFunctions';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';

const AboutSection = React.memo(() => {
  const { i18n } = useTranslation();

  const langCode = useMemo(() => 
    (i18n?.language ? i18n.language.slice(0, 2).toLowerCase() : 'fr'),
    [i18n?.language]
  );

  const aboutContent = useMemo(() => ({
    fr: {
      headline: 'Pourquoi choisir Allô Driver, le VTC N°1 en France ?',
      subline: 'Le service de chauffeur privé VTC préféré des Français. Réservez en ligne ou via notre app VTC en quelques secondes.',
      stats: [
        {
          icon: DirectionsCarIcon,
          label: 'Avis clients vérifiés',
          value: '4.9/5'
        },
        {
          icon: LocationCityIcon,
          label: 'Villes couvertes',
          value: 'Paris, Lyon, Marseille, Toulouse, Bordeaux...'
        },
        {
          icon: DirectionsCarIcon,
          label: 'Flotte VTC verte',
          value: '100% Hybride & Électrique'
        },
        {
          icon: CalendarMonthIcon,
          label: 'Service VTC',
          value: 'Disponible 24h/7j'
        }
      ],
      features: [
        'VTC France : service disponible dans toutes les grandes villes',
        'Chauffeur privé professionnel, formé et certifié',
        'Réservation VTC en ligne sans inscription en 30 secondes',
        'Prix fixe garanti, pas de surprise sur le tarif',
        'Flotte 100% hybride et électrique, éco-responsable',
        'VTC aéroport : transferts vers tous les aéroports français',
        'VTC gare : prise en charge à toutes les gares SNCF',
        'VTC longue distance : trajets inter-villes confortables',
        'Application VTC iOS et Android avec suivi GPS temps réel',
        'Service client disponible 7j/7 au 06 69 67 52 82'
      ]
    },
    en: {
      headline: 'Why choose Allô Driver, France\'s #1 VTC Service?',
      subline: 'The preferred private chauffeur service in France. Book online or via our VTC app in seconds.',
      stats: [
        {
          icon: DirectionsCarIcon,
          label: 'Verified Customer Reviews',
          value: '4.9/5'
        },
        {
          icon: LocationCityIcon,
          label: 'Cities Covered',
          value: 'Paris, Lyon, Marseille, Toulouse, Bordeaux...'
        },
        {
          icon: DirectionsCarIcon,
          label: 'Green VTC Fleet',
          value: '100% Hybrid & Electric'
        },
        {
          icon: CalendarMonthIcon,
          label: 'VTC Service',
          value: 'Available 24/7'
        }
      ],
      features: [
        'VTC France: service available in all major cities',
        'Professional private chauffeur, trained and certified',
        'Book your VTC online without registration in 30 seconds',
        'Fixed price guaranteed, no surprises on the fare',
        '100% hybrid and electric fleet, eco-friendly',
        'Airport VTC: transfers to all French airports',
        'Train station VTC: pickup at all SNCF stations',
        'Long distance VTC: comfortable inter-city rides',
        'VTC app for iOS and Android with real-time GPS tracking',
        'Customer service available 7 days a week at +33 6 69 67 52 82'
      ]
    }
  }), []);

  const content = useMemo(() => 
    aboutContent[langCode] || aboutContent.fr,
    [aboutContent, langCode]
  );

  return (
    <Box sx={{ 
      py: { xs: 6, md: 10 }, 
      px: { xs: 2, md: 4 },
      backgroundColor: 'transparent',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Decorative background */}
      <Box sx={{
        position: 'absolute',
        top: '-50%',
        right: '-20%',
        width: '600px',
        height: '600px',
        borderRadius: '50%',
        background: `radial-gradient(circle, rgba(212, 175, 55, 0.1) 0%, transparent 70%)`,
        pointerEvents: 'none'
      }} />

      <Box sx={{ maxWidth: '1200px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
        {/* Stats Grid */}
        <Grid container spacing={3} justifyContent="center">
          {content.stats.map((stat, index) => {
            const IconComponent = stat.icon;
            return (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Box sx={{
                  textAlign: 'center',
                  p: 4,
                  borderRadius: '16px',
                  backgroundColor: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(212, 175, 55, 0.2)',
                  transition: 'all 0.3s ease',
                  height: '100%',
                  '&:hover': {
                    backgroundColor: 'rgba(212, 175, 55, 0.08)',
                    borderColor: colors.Button_Primary,
                    transform: 'translateY(-5px)',
                    boxShadow: `0 12px 32px rgba(212, 175, 55, 0.2)`
                  }
                }}>
                  <Box sx={{
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                    backgroundColor: 'rgba(212, 175, 55, 0.15)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 16px',
                    border: `2px solid ${colors.Button_Primary}`
                  }}>
                    <IconComponent sx={{ fontSize: 30, color: colors.Button_Primary }} />
                  </Box>
                  
                  <Typography 
                    sx={{ 
                      fontFamily: FONT_FAMILY,
                      fontSize: '14px',
                      color: 'rgba(255,255,255,0.6)',
                      mb: 1,
                      textTransform: 'uppercase',
                      letterSpacing: '1px',
                      fontWeight: 600
                    }}
                  >
                    {stat.label}
                  </Typography>
                  
                  <Typography 
                    sx={{ 
                      fontFamily: FONT_FAMILY,
                      fontSize: '20px',
                      color: '#fff',
                      fontWeight: 700
                    }}
                  >
                    {stat.value}
                  </Typography>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    </Box>
  );
});

export default AboutSection;
